import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { Dispatch, FC, SetStateAction } from "react";
import { useSuperAdminPermissions } from "../../../../context/SuperAdmin/PermissionContext";

interface Props {
  selectedComponents: string[];
  setSelectedComponents: Dispatch<SetStateAction<string[]>>;
}

const OtherAppsMenu: FC<Props> = ({
  selectedComponents,
  setSelectedComponents,
}) => {
  const { missingComponents, isAdding } = useSuperAdminPermissions();

  // Función para manejar la selección/deselección individual
  const handleCheckboxChange = (id: string) => {
    setSelectedComponents((prev) =>
      prev.includes(id) ? prev.filter((comp) => comp !== id) : [...prev, id]
    );
  };

  // Función para seleccionar o deseleccionar todos
  const toggleSelectAll = () => {
    if (selectedComponents.length > 0) {
      setSelectedComponents([]); // Si todos están seleccionados, deselecciona
    } else {
      setSelectedComponents(
        missingComponents.map((comp) => comp.id_componente)
      ); // Selecciona todos
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        border: "1px solid rgba(51, 107, 135, 0.5)", // Borde con opacidad
        borderRadius: "8px", // Bordes redondeados
        padding: "8px 16px", // Padding general
        transition: "all 0.3s ease", // Suaviza la transición
      }}
    >
      {/* Título */}
      <Typography variant="h6" component="h2" color="#000">
        Otras UmmIApps{" "}
        <Typography component="span" color={"#73767E"} fontSize={"20px"}>
          {selectedComponents.length > 0
            ? `(${selectedComponents.length}/${missingComponents.length})`
            : `(${missingComponents.length})`}
        </Typography>
      </Typography>

      {/* Botón de Seleccionar/Deseleccionar todos */}
      {missingComponents.length > 0 && (
        <Button
          onClick={toggleSelectAll}
          variant="outlined"
          disabled={isAdding}
          sx={{
            width: "100%",
            marginTop: "8px",
            textTransform: "none",
            color: "#336B87",
            borderColor: "rgba(51, 107, 135, 0.5)",
            "&:hover": {
              borderColor: "#336B87",
              backgroundColor: "rgba(51, 107, 135, 0.1)",
            },
          }}
        >
          {selectedComponents.length > 0
            ? "Deseleccionar todos"
            : "Seleccionar todos"}
        </Button>
      )}

      {/* Contenido con scroll si excede los 350px */}
      <Box
        sx={{
          marginTop: "8px",
          paddingTop: "8px",
          borderTop: "1px solid rgba(51, 107, 135, 0.5)", // Separador sutil
          maxHeight: "350px", // Máximo de altura
          overflowY: "auto", // Scroll vertical si es necesario
        }}
      >
        {missingComponents.length > 0 ? (
          missingComponents.map((componente) => (
            <FormControlLabel
              key={componente.id_componente}
              control={
                <Checkbox
                  checked={selectedComponents.includes(
                    componente.id_componente
                  )}
                  disabled={isAdding}
                  onChange={() =>
                    handleCheckboxChange(componente.id_componente)
                  }
                  sx={{
                    color: "#336B87",
                    "&.Mui-checked": { color: "#336B87" },
                  }} // Color del checkbox
                />
              }
              label={<Typography color="#000">{componente.nombre}</Typography>}
              sx={{ display: "flex", alignItems: "center" }}
            />
          ))
        ) : (
          <Typography color="#73767E" fontStyle="italic" textAlign="center">
            No hay otras apps disponibles
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default OtherAppsMenu;
